import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import CesiumDrawingFeature from "./CesiumDrawingFeature";
import type { Feature, FeatureCollection, Geometry, Position } from "geojson";
import { geminiRequest, useGlobalState } from "senf-shared";
import styled, { keyframes } from "styled-components";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import useIdeaPinCesium from "./hooks/useIdeaPinCesium";
import IdeaPin from "../../../assets/icons/IdeaPin";

// Add type declarations for Cesium
declare global {
  interface Window {
    Cesium: any;
  }
}

interface CesiumMapProps {
  onCesiumCameraMove?: (position: { center: [number, number]; height: number; bearing: number; pitch: number }) => void;
  cesiumCameraPosition?: { center: [number, number]; height: number; bearing?: number; pitch?: number };
  buildingStyle?: string;
  distanceCenter?: number;
  onDrawComplete?: (featureCollection: GeoJSON.FeatureCollection) => void;
  gisData?: Array<{
    data: FeatureCollection;
    layerName?: string;
    layerTitle?: string;
    layerType?: string;
    [key: string]: any;
  }>;
  pinData?: any;
  onScreenshotTaken?: (screenshotUrl: string) => void;
  geminiApiKey?: string;
  mapStyle?: string;
  enableTileCache?: boolean;
  maxTileCacheSize?: number;
  fogColor?: string;
  fogDensity?: number;
  fogByDistance?: { start: number; startValue: number; end: number; endValue: number };
  handleClickIdeaMarker?: (ideaId: string) => void;
}

// Add a new interface for the center coordinates
interface CesiumCenterCoordinates {
  longitude: number;
  latitude: number;
}

async function initializeViewer(
  Cesium: any, 
  isMobile: boolean,
  onCesiumCameraMove?: CesiumMapProps['onCesiumCameraMove'], 
  cesiumCameraPosition?: CesiumMapProps['cesiumCameraPosition'], 
  osmBuildingsLoadedRef?: React.MutableRefObject<boolean>, 
  onScreenshotTaken?: CesiumMapProps['onScreenshotTaken'], 
  enableTileCache: boolean = true, 
  maxTileCacheSize: number = 512,
  fogColor?: string,
  fogDensity?: number,
  fogByDistance?: { start: number; startValue: number; end: number; endValue: number },
) {
  // Configure tile cache if enabled
  if (enableTileCache && Cesium.createTileCache) {
    // Reduce logging for performance
    // console.log("CesiumMap: Setting up tile cache with max size:", maxTileCacheSize, "MB");
    const tileCache = Cesium.createTileCache({
      storage: new Cesium.TileCache.LocalStorage(), // Use local storage
      maxMemoryUsage: 1024 * 1024 * maxTileCacheSize, // Convert MB to bytes
    });
    
    // Apply the cache to Cesium
    Cesium.setTileCache(tileCache);
  }
  
  // OPTIMIZATION: Use a lower quality terrain provider initially
  const initialTerrainProvider = new Cesium.EllipsoidTerrainProvider();
  
  const viewer = new Cesium.Viewer("cesiumContainer", {
    terrainProvider: initialTerrainProvider, // Start with flat terrain for faster loading
    sceneMode: Cesium.SceneMode.SCENE3D,
    scene3DOnly: true,
    navigationHelpButton: false,
    geocoder: false,
    homeButton: false,
    baseLayerPicker: false,
    timeline: false,
    animation: false,
    fullscreenButton: false,
    vrButton: false,
    selectionIndicator: false,
    infoBox: false,
    requestRenderMode: true,
    imageryProvider: false, // Disable default imagery
    // OPTIMIZATION: Set a lower target frame rate for better performance
    targetFrameRate: 30,
    // OPTIMIZATION: Disable shadows for faster rendering
    shadows: false,
    // PREVENT BLUE FLASHES: Set default globe color to match imagery
    baseColor: new Cesium.Color(0.0, 0.0, 0.0, 0.0),
    // Enable anti-aliasing for smoother edges on railings and details
    msaaSamples: 2 // Reduced from 4 for better performance
  });
  
  // Ensure the viewer takes up 100% of its container
  const cesiumViewerElement = document.querySelector(".cesium-viewer");
  const cesiumWidgetElement = document.querySelector(".cesium-widget");
  const cesiumCanvasElement = document.querySelector(".cesium-widget canvas");
  
  if (cesiumViewerElement) {
    const viewerStyle = (cesiumViewerElement as HTMLElement).style;
    viewerStyle.width = "100%";
    viewerStyle.height = "100%";
    viewerStyle.position = "absolute";
    viewerStyle.top = "0";
    viewerStyle.left = "0";
    viewerStyle.right = "0";
    viewerStyle.bottom = "0";
  }
  
  if (cesiumWidgetElement) {
    const widgetStyle = (cesiumWidgetElement as HTMLElement).style;
    widgetStyle.width = "100%";
    widgetStyle.height = "100%";
    widgetStyle.position = "absolute";
    widgetStyle.top = "0";
    widgetStyle.left = "0";
    widgetStyle.right = "0";
    widgetStyle.bottom = "0";
  }
  
  if (cesiumCanvasElement) {
    const canvasStyle = (cesiumCanvasElement as HTMLElement).style;
    canvasStyle.width = "100%";
    canvasStyle.height = "100%";
    canvasStyle.position = "absolute";
    canvasStyle.top = "0";
    canvasStyle.left = "0";
    canvasStyle.right = "0";
    canvasStyle.bottom = "0";
  }


// const viewModel = {
//   show: true,
//   ambientOcclusionOnly: false,
//   intensity: 3.0,
//   bias: 0.1,
//   lengthCap: 0.26,
//   directionCount: 8,
//   stepCount: 32,
// };

  
  // PREVENT BLUE FLASHES: Make sure the globe is transparent during transitions
  viewer.scene.globe.translucency.enabled = true;
  viewer.scene.globe.translucency.frontFaceAlpha = 1.0;
  
  // // Add custom fog effect
  // try {
  //   if (viewer.scene.context.depthTexture) {
  //     console.log("CesiumMap: Adding custom fog post-process effect");
      
  //     // Define the custom fog shader
  //     const fogFragmentShaderSource = `
  //       float getDistance(sampler2D depthTexture, vec2 texCoords)
  //       {
  //           float depth = czm_unpackDepth(texture(depthTexture, texCoords));
  //           if (depth == 0.0) {
  //               return czm_infinity;
  //           }
  //           vec4 eyeCoordinate = czm_windowToEyeCoordinates(gl_FragCoord.xy, depth);
  //           return -eyeCoordinate.z / eyeCoordinate.w;
  //       }
  //       float interpolateByDistance(vec4 nearFarScalar, float distance)
  //       {
  //           float startDistance = nearFarScalar.x;
  //           float startValue = nearFarScalar.y;
  //           float endDistance = nearFarScalar.z;
  //           float endValue = nearFarScalar.w;
  //           float t = clamp((distance - startDistance) / (endDistance - startDistance), 0.0, 1.0);
  //           return mix(startValue, endValue, t);
  //       }
  //       vec4 alphaBlend(vec4 sourceColor, vec4 destinationColor)
  //       {
  //           return sourceColor * vec4(sourceColor.aaa, 1.0) + destinationColor * (1.0 - sourceColor.a);
  //       }
  //       uniform sampler2D colorTexture;
  //       uniform sampler2D depthTexture;
  //       uniform vec4 fogByDistance;
  //       uniform vec4 fogColor;
  //       in vec2 v_textureCoordinates;
  //       void main(void)
  //       {
  //           float distance = getDistance(depthTexture, v_textureCoordinates);
  //           vec4 sceneColor = texture(colorTexture, v_textureCoordinates);
  //           float blendAmount = interpolateByDistance(fogByDistance, distance);
  //           vec4 finalFogColor = vec4(fogColor.rgb, fogColor.a * blendAmount);
  //           out_FragColor = alphaBlend(finalFogColor, sceneColor);
  //       }
  //     `;
      
  //     // Set default fog values based on props
  //     const defaultFogColor = fogColor ? Cesium.Color.fromCssColorString(fogColor).withAlpha(0.7) : Cesium.Color.WHITE.withAlpha(0.3);
      
  //     // Calculate byDistance values based on fogDensity if provided
  //     let fogByDistanceCartesian;
  //     if (fogByDistance) {
  //       // Use custom fogByDistance values if provided
  //       fogByDistanceCartesian = new Cesium.Cartesian4(
  //         fogByDistance.start, 
  //         fogByDistance.startValue, 
  //         fogByDistance.end, 
  //         fogByDistance.endValue
  //       );
  //     } else if (fogDensity !== undefined) {
  //       // Strengthen fog effect by making density more impactful
  //       const adjustedDensity = Math.min(1.0, fogDensity * 2.0); // Amplify density by 2.0x, max 1.0
        
  //       // Higher density = shorter distances and higher end value
  //       const startDistance = 1;
  //       const startValue = 0.0;
  //       // Make fog appear closer with higher density
  //       const endDistance = 100 * (1 - fogDensity * 0.95); // Reduce end distance more with higher density
  //       const endValue = adjustedDensity + 0.5; // Increase minimum to 0.5, max 1.5 for stronger fog
        
  //       fogByDistanceCartesian = new Cesium.Cartesian4(startDistance, startValue, endDistance, endValue);
  //     } else {
  //       // Default values if no props provided - stronger black fog
  //       fogByDistanceCartesian = new Cesium.Cartesian4(5, 0.0, 500, 1.2);
  //     }
      
  //     // Create and add the post-process stage
  //     const fogPostProcessStage = viewer.scene.postProcessStages.add(
  //       new Cesium.PostProcessStage({
  //         name: 'custom-fog',
  //         fragmentShader: fogFragmentShaderSource,
  //         uniforms: {
  //           fogByDistance: fogByDistanceCartesian,
  //           fogColor: defaultFogColor,
  //         },
  //       })
  //     );
      
  //     console.log("CesiumMap: Fog post-process effect added successfully with:", {
  //       color: defaultFogColor.toString(),
  //       byDistance: [
  //         fogByDistanceCartesian.x,
  //         fogByDistanceCartesian.y,
  //         fogByDistanceCartesian.z,
  //         fogByDistanceCartesian.w
  //       ]
  //     });
      
  //     // Add method to update fog parameters
  //     viewer.updateFog = (options) => {
  //       if (fogPostProcessStage && !viewer.isDestroyed()) {
  //         if (options.color) {
  //           if (typeof options.color === 'string') {
  //             fogPostProcessStage.uniforms.fogColor = Cesium.Color.fromCssColorString(options.color);
  //           } else {
  //             fogPostProcessStage.uniforms.fogColor = options.color;
  //           }
  //         }
  //         if (options.byDistance) {
  //           fogPostProcessStage.uniforms.fogByDistance = options.byDistance;
  //         } else if (options.density !== undefined) {
  //           const density = Math.max(0, Math.min(1, options.density));
  //           // Strengthen fog effect by making density more impactful
  //           const adjustedDensity = Math.min(1.0, density * 2.0); // Amplify density by 2.0x, max 1.0
  //           // Make fog appear closer with higher density
  //           const endDistance = 100 * (1 - density * 0.95); // Reduce end distance more with higher density
  //           const endValue = adjustedDensity + 0.5; // Increase minimum to 0.5, max 1.5 for stronger fog
            
  //           fogPostProcessStage.uniforms.fogByDistance = new Cesium.Cartesian4(
  //             10, 0.0, endDistance, endValue
  //           );
  //         }
  //         viewer.scene.requestRender();
  //       }
  //     };
  //   } else {
  //     console.warn("CesiumMap: This browser does not support the fog post process (no depth texture support)");
  //   }
  // } catch (error) {
  //   console.error("CesiumMap: Error setting up fog effect:", error);
  // }
  
  // OPTIMIZATION: Enable foveated rendering (render center at higher quality than edges)
  if (viewer.scene.foveatedScreenSpaceError !== undefined) {
    viewer.scene.foveatedScreenSpaceError = true;
    viewer.scene.foveatedConeSize = 0.1;
    viewer.scene.foveatedMinimumScreenSpaceErrorRelaxation = 0.0;
  }
  
  // OPTIMIZATION: Start with lighting disabled
  viewer.scene.globe.enableLighting = true;
  
  // Enable shadows from the beginning
  // viewer.shadows = true;
  // viewer.terrainShadows = Cesium.ShadowMode.ENABLED;
  
  // // OPTIMIZATION: Enable high dynamic range if available
  // if (viewer.scene.highDynamicRange !== undefined) {
  //   viewer.scene.highDynamicRange = true;
  // }
  
  // // Set atmosphere and lighting options for better visuals
  // viewer.scene.globe.showGroundAtmosphere = true;
  // viewer.scene.globe.enableLighting = true;
  // viewer.scene.globe.atmosphereLightIntensity = 5.0;
  // viewer.scene.globe.atmosphereHue = 0.5;
  // viewer.scene.globe.atmosphereSaturation = 1.5;
  // viewer.scene.globe.atmosphereBrightness = 1.5;
  
  // // Enhanced sky atmosphere settings
  // viewer.scene.skyAtmosphere.hueShift = 0.0;
  // viewer.scene.skyAtmosphere.saturationShift = 0.1;
  // viewer.scene.skyAtmosphere.brightnessShift = 0.1;
  
  // // Set lighting to mid-afternoon for attractive shadows
  // const julianDate = Cesium.JulianDate.fromDate(new Date());
  // const hour = 15; // 3pm
  // const minute = 30;
  // julianDate.secondsOfDay = hour * 3600 + minute * 60;
  // viewer.clock.currentTime = julianDate;
  // viewer.clock.multiplier = 0; // Freeze time
  
  // // Enhance ground appearance
  // if (viewer.scene.globe.material) {
  //   // Add slight color tint to terrain
  //   const material = new Cesium.Material({
  //     fabric: {
  //       type: 'DiffuseMap',
  //       uniforms: {
  //         color: new Cesium.Color(1.0, 1.0, 1.0, 1.0),
  //         brightness: 1.05
  //       }
  //     }
  //   });
  //   viewer.scene.globe.material = material;
  // }
  
  // // Enhance fog settings for depth perception
  // viewer.scene.fog.enabled = true;
  // viewer.scene.fog.density = 0.0001;
  // viewer.scene.fog.screenSpaceErrorFactor = 4.0;
  // viewer.scene.fog.minimumBrightness = 0.1;
  
  // // Force advanced rendering options
  // viewer.scene.logarithmicDepthBuffer = true;
  // if (viewer.scene.globe.maximumScreenSpaceError !== undefined) {
  //   viewer.scene.globe.maximumScreenSpaceError = 1.0; // Lower value = higher terrain quality
  // }
  
  // // Set up nice shadow settings
  // viewer.shadowMap.enabled = true;
  // viewer.shadowMap.softShadows = true;
  // viewer.shadowMap.size = 2048;
  // viewer.shadowMap.darkness = 0.5;
  

  
  // Remove all default imagery layers
  viewer.imageryLayers.removeAll();
  
  // OPTIMIZATION: Add OpenStreetMap imagery layer with lower initial maximum level
  const osmLayer = viewer.imageryLayers.addImageryProvider(
    new Cesium.MapboxImageryProvider({
      mapId: 'mapbox.satellite',
      accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
      maximumLevel: 19 // Load high-resolution directly
    })
  );
  // Remove the commented out high-res layer code that was never used
  // const highResLayer = viewer.imageryLayers.addImageryProvider(osmLayer);
  // highResLayer.alpha = 1;
  
  // PREVENT BLUE FLASHES: Make sure imagery layer is fully opaque
  osmLayer.alpha = 1.0;
  
  viewer.scene.globe.depthTestAgainstTerrain = true;

  // Set the time to 15:00 on April 1st
  const aprilFirst = new Date();
  aprilFirst.setMonth(3); // April (0-indexed)
  aprilFirst.setDate(1);
  aprilFirst.setHours(15, 0, 0, 0);
  viewer.clock.currentTime = Cesium.JulianDate.fromDate(aprilFirst);
  
  // Get the camera controller
  const controller = viewer.scene.screenSpaceCameraController;

  // Reset all event types to default
  controller.tiltEventTypes = [Cesium.CameraEventType.RIGHT_DRAG];
  controller.rotateEventTypes = [Cesium.CameraEventType.LEFT_DRAG];
  controller.zoomEventTypes = [Cesium.CameraEventType.WHEEL, Cesium.CameraEventType.PINCH];
  controller.translateEventTypes = [Cesium.CameraEventType.MIDDLE_DRAG];

  // Enable all navigation controls
  controller.enableRotate = true;
  controller.enableTranslate = true;
  controller.enableZoom = true;
  controller.enableTilt = true;

  // Set minimum and maximum zoom distances
  controller.minimumZoomDistance = 1.0;
  controller.maximumZoomDistance = 50000000.0;

  // Set minimum and maximum tilt angles (in radians)
  controller.minimumPitch = 0.0;
  controller.maximumPitch = Cesium.Math.PI_OVER_TWO;

  // Configure camera constraints
  viewer.scene.screenSpaceCameraController.inertiaSpin = 0.0;
  viewer.scene.screenSpaceCameraController.inertiaTranslate = 0.0;
  viewer.scene.screenSpaceCameraController.inertiaZoom = 0.0;

 

  // Add camera move end event listener
  viewer.camera.moveEnd.addEventListener(() => {
    
   
    const cartographic = Cesium.Cartographic.fromCartesian(viewer.camera.position);
    const cameraPosition: {
          center: [number, number];
          pitch: number;
          bearing: number;
          height: number;
        } = {
      center: [Cesium.Math.toDegrees(cartographic.longitude), Cesium.Math.toDegrees(cartographic.latitude)],
      height: cartographic.height,
      bearing: Cesium.Math.toDegrees(viewer.camera.heading),
      pitch: Cesium.Math.toDegrees(viewer.camera.pitch),
    }


    
    
    onCesiumCameraMove?.(cameraPosition);
  });

  // Add change event listener for continuous updates
  viewer.camera.changed.addEventListener(() => {
    // No need to log every change event
  });

  // Add move start event listener for debugging
  viewer.camera.moveStart.addEventListener(() => {
    // console.log('Camera move start event triggered');
  });

  // Set initial camera position if provided
  if (cesiumCameraPosition) {
    // Ensure height is appropriate - minimum 100 meters to prevent going underground
    const height = Math.max(100, cesiumCameraPosition.height || 1000);
    
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        cesiumCameraPosition.center[0],
        cesiumCameraPosition.center[1],
        height
      ),
      orientation: {
        heading: cesiumCameraPosition.bearing ? Cesium.Math.toRadians(cesiumCameraPosition.bearing) : 0,
        pitch: cesiumCameraPosition.pitch ? Cesium.Math.toRadians(cesiumCameraPosition.pitch) : -Cesium.Math.PI_OVER_TWO,
        roll: 0.0
      },
      duration: 2.0
    });
  }

  const [_, ...rest] = document.querySelectorAll(".cesium-viewer");

  const cesiumContainer = document.querySelectorAll(
    ".cesium-viewer-bottom, .cesium-viewer-selectionIndicatorContainer, .cesium-viewer-infoBoxContainer, .cesium-viewer-infoBoxContainer, .cesium-viewer-toolbar, .cesium-viewer-timelineContainer, .cesium-viewer-fullscreenContainer, .cesium-viewer-animationContainer",
  );
  for (const element of [...cesiumContainer, ...rest]) {
    element.remove();
  }

  // Additional styling for canvas element - in case it wasn't styled earlier
  const cesiumCanvas = document.querySelector(".cesium-widget canvas");
  if (cesiumCanvas) {
    const canvasStyle = (cesiumCanvas as HTMLElement).style;
    canvasStyle.width = "100%";
    canvasStyle.height = "100%";
    canvasStyle.position = "absolute";
    canvasStyle.top = "0";
    canvasStyle.left = "0";
    canvasStyle.right = "0";
    canvasStyle.bottom = "0";
  }

  try {
    const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(3279851, {
      // OPTIMIZATION: Set higher maximum screen space error for faster initial loading
      maximumScreenSpaceError: 32, // Lower value = higher quality (reduced from 32)
      // shadows: Cesium.ShadowMode.ENABLED, // Enable shadows on the tileset
    });

    if(enableTileCache){
    
    // OPTIMIZATION: Apply optimized caching and loading settings
    tileset.maximumMemoryUsage = maxTileCacheSize ? maxTileCacheSize * 1024 * 1024 : 512 * 1024 * 1024;
    
    // OPTIMIZATION: Temporarily disable preloading during initial load
    tileset.preloadWhenHidden = true;
    tileset.preloadFlightDestinations = true;
    tileset.preferLeaves = true;
    
    // OPTIMIZATION: Configure how aggressively the tileset should cache and load
    tileset.cullRequestsWhileMoving = false; // Cancel requests when moving initially
    tileset.cullWithChildrenBounds = true;
    tileset.skipLevelOfDetail = true;
      
  
    // 
    //  Don't load siblings to focus on what's visible
    }

    // OPTIMIZATION: Add more skipLevelOfDetail-related settings
    tileset.skipScreenSpaceErrorFactor = 4; // Reduced from 8 for better quality transitions
    tileset.skipLevels = 0; // Reduced from 1 to show all detail levels
    tileset.immediatelyLoadDesiredLevelOfDetail = true; // Load desired LOD immediately
    tileset.loadSiblings = true; // Changed to true to improve visual quality around edges
    
    viewer.scene.primitives.add(tileset);
    
    // Use zoomTo instead of a long await to get the initial view rendered faster
    // viewer.zoomTo(tileset, new Cesium.HeadingPitchRange(0, -Math.PI/8, 0));
    
    // Apply the default style if it exists
    const extras = tileset.asset.extras;
    if (
      Cesium.defined(extras) &&
      Cesium.defined(extras.ion) &&
      Cesium.defined(extras.ion.defaultStyle)
    ) {
      tileset.style = new Cesium.Cesium3DTileStyle(extras.ion.defaultStyle);
    }

    // console.log("INITIAL INITIALIZE", cesiumCameraPosition)
    // Set camera position after model is loaded
    if (cesiumCameraPosition) {
      // Ensure height is appropriate - minimum 100 meters to prevent going underground
      const height = Math.max(100, cesiumCameraPosition.height || 1000);
      
      // console.log("SETTING: CAMERAPOSITION", cesiumCameraPosition)
      viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          cesiumCameraPosition.center[0],
          cesiumCameraPosition.center[1],
          height
        ),
        orientation: {
          heading: cesiumCameraPosition.bearing ? Cesium.Math.toRadians(cesiumCameraPosition.bearing) : 0,
          pitch: cesiumCameraPosition.pitch ? Cesium.Math.toRadians(cesiumCameraPosition.pitch) : -Cesium.Math.PI_OVER_TWO,
          roll: 0.0
        },
        duration: 2.0
      });
    }
    
    // Initial terrain setup
    Cesium.createWorldTerrainAsync({
      // requestWaterMask: true,
      // requestVertexNormals: true,
      // requestMetadata: true
    }).then(terrainProvider => {
      if (viewer && !viewer.isDestroyed()) {
        viewer.terrainProvider = terrainProvider;        
        // Force a render after terrain is loaded
        viewer.scene.requestRender();
      }
    }).catch(error => {
      console.warn('CesiumMap: Failed to initialize terrain provider:', error);
    });
    
  } catch (error) {
    console.log(error);
  }

  // Add a method to load OSM buildings
  viewer.loadOsmBuildings = async () => {
    try {      
      // PREVENT BLUE FLASHES: Force a render to ensure the scene is stable before adding buildings
      viewer.scene.requestRender();
      
      // Add Cesium OSM buildings to the scene
      const osmBuildingsTileset = await Cesium.createOsmBuildingsAsync({
        // OPTIMIZATION: Use higher maximum screen space error for initial OSM buildings load
        maximumScreenSpaceError: 64, // Increased from 128 for better performance
        // shadows: Cesium.ShadowMode.ENABLED // Enable shadows on OSM buildings
      });
      
      // Apply caching options if enabled
      if (enableTileCache) {
        // Configure cache settings for OSM buildings - use more conservative settings
        osmBuildingsTileset.maximumMemoryUsage = maxTileCacheSize ? maxTileCacheSize * 1024 * 1024 : 512 * 1024 * 1024;
        
        // Keep preloading enabled from the start for OSM buildings to avoid black screen issues
        osmBuildingsTileset.preloadWhenHidden = true;
        osmBuildingsTileset.preloadFlightDestinations = true;
        osmBuildingsTileset.preferLeaves = true;
        
        // Use less aggressive culling to prevent black screen issues
        osmBuildingsTileset.cullRequestsWhileMoving = false;
        osmBuildingsTileset.cullWithChildrenBounds = true;
        osmBuildingsTileset.skipLevelOfDetail = true;
        
        // OPTIMIZATION: Add more skipLevelOfDetail-related settings
        osmBuildingsTileset.skipScreenSpaceErrorFactor = 64; // Increased from 32 for better performance
        osmBuildingsTileset.skipLevels = 1; // Increased from 0 to skip more detail levels
        osmBuildingsTileset.immediatelyLoadDesiredLevelOfDetail = true;
        osmBuildingsTileset.loadSiblings = false; // Disabled to reduce memory usage
      }
      
      viewer.scene.primitives.add(osmBuildingsTileset);
      // console.log("OSM buildings loaded successfully");
      // Update the ref to indicate OSM buildings are loaded
      if (osmBuildingsLoadedRef) {
        osmBuildingsLoadedRef.current = true;
      }
    } catch (error) {
      console.log("Error loading OSM buildings:", error);
    }
  };

  // Add a method to take a screenshot
  viewer.takeScreenshot = () => {
    try {
      // Render the scene to ensure it's up to date
      viewer.render();
      
      // Get the canvas element
      const canvas = viewer.canvas;
      
      // Create a data URL from the canvas
      const screenshotUrl = canvas.toDataURL('image/png');
      
      // Call the callback with the screenshot URL
      onScreenshotTaken?.(screenshotUrl);
      
      // console.log('Screenshot taken successfully');
      return screenshotUrl;
    } catch (error) {
      console.error('Error taking screenshot:', error);
      return null;
    }
  };

  // Add methods for tile cache management
  if (enableTileCache && Cesium.getTileCache) {
    // Method to clear the tile cache
    viewer.clearTileCache = () => {
      try {
        const cache = Cesium.getTileCache();
        if (cache) {
          cache.clear();
          console.log('CesiumMap: Tile cache cleared successfully');
          return true;
        }
        console.log('CesiumMap: No tile cache to clear');
        return false;
      } catch (error) {
        console.error('Error clearing tile cache:', error);
        return false;
      }
    };
    
    // Method to get tile cache stats for debugging
    viewer.getTileCacheStats = () => {
      try {
        const cache = Cesium.getTileCache();
        if (cache) {
          const stats = {
            size: cache.size || 0,
            maxSize: cache.maxSize || 0,
            usedPercentage: cache.size && cache.maxSize ? (cache.size / cache.maxSize) * 100 : 0,
            itemCount: cache.length || 0
          };
          console.log('CesiumMap: Tile cache stats:', stats);
          return stats;
        }
        return null;
      } catch (error) {
        console.error('Error getting tile cache stats:', error);
        return null;
      }
    };
    
    // Method to preload tiles for a specific area
    viewer.preloadTilesForArea = async (rectangle, maximumLevel = 15) => {
      try {
        // console.log('CesiumMap: Preloading tiles for area', rectangle);
        
        // Create a rectangle if coordinates were passed instead of a Rectangle object
        let tileRectangle;
        if (Array.isArray(rectangle)) {
          // rectangle format: [west, south, east, north] in degrees
          tileRectangle = Cesium.Rectangle.fromDegrees(
            rectangle[0], rectangle[1], rectangle[2], rectangle[3]
          );
        } else if (typeof rectangle === 'object' && 
                  'west' in rectangle && 'south' in rectangle && 
                  'east' in rectangle && 'north' in rectangle) {
          tileRectangle = Cesium.Rectangle.fromDegrees(
            rectangle.west, rectangle.south, rectangle.east, rectangle.north
          );
        } else {
          throw new Error('Invalid rectangle format. Expected array [west, south, east, north] or object with those properties.');
        }
        
        // For each imagery and terrain layer, request tiles
        const imageryLayers = viewer.imageryLayers;
        for (let i = 0; i < imageryLayers.length; i++) {
          const layer = imageryLayers.get(i);
          const provider = layer.imageryProvider;
          
          // Request preloading of tiles for the rectangle
          if (provider && provider.requestImage && provider.tilingScheme) {
            await preloadTilesForProvider(provider, tileRectangle, maximumLevel);
          }
        }
        
        // Preload terrain tiles
        const terrainProvider = viewer.terrainProvider;
        if (terrainProvider && terrainProvider.requestTileGeometry) {
          await preloadTilesForProvider(terrainProvider, tileRectangle, maximumLevel);
        }
        
        console.log('CesiumMap: Finished preloading tiles');
        return true;
      } catch (error) {
        console.error('Error preloading tiles:', error);
        return false;
      }
    };
    
    // Helper function to preload tiles for a specific provider
    async function preloadTilesForProvider(provider, rectangle, maximumLevel) {
      // Get the relevant tile coordinates that cover the rectangle
      const tilingScheme = provider.tilingScheme;
      
      // For each zoom level
      for (let level = 0; level <= maximumLevel; level++) {
        // Get the tile coordinates that cover the rectangle at this level
        const xyzRange = tilingScheme.positionToTileXY(rectangle, level);
        if (!xyzRange) continue;
        
        const { x, y } = xyzRange;
        const xMin = Math.max(0, x.min);
        const yMin = Math.max(0, y.min);
        const xMax = Math.min(tilingScheme.getNumberOfXTilesAtLevel(level) - 1, x.max);
        const yMax = Math.min(tilingScheme.getNumberOfYTilesAtLevel(level) - 1, y.max);
        
        // Request each tile
        for (let x = xMin; x <= xMax; x++) {
          for (let y = yMin; y <= yMax; y++) {
            try {
              // For imagery layers
              if (provider.requestImage) {
                await provider.requestImage(x, y, level);
              }
              // For terrain provider
              else if (provider.requestTileGeometry) {
                await provider.requestTileGeometry(x, y, level);
              }
            } catch (e) {
              // Quietly ignore errors for individual tiles
              console.warn(`Failed to preload tile at ${x},${y},${level}:`, e);
            }
          }
        }
      }
    }
  }

  // Force advanced rendering options
  viewer.scene.logarithmicDepthBuffer = true;
  if (viewer.scene.globe.maximumScreenSpaceError !== undefined) {
    viewer.scene.globe.maximumScreenSpaceError = 0.5; // Lower value = higher terrain quality
  }
  
  // Enable anti-aliasing for smoother edges
  viewer.scene.postProcessStages.fxaa.enabled = true;
  
  // Improve texture filtering
  viewer.scene.globe.minificationFilter = Cesium.TextureMinificationFilter.LINEAR;
  viewer.scene.globe.magnificationFilter = Cesium.TextureMagnificationFilter.LINEAR;
  
  // Set up nice shadow settings
  viewer.shadowMap.enabled = true;
  viewer.shadowMap.softShadows = true;
  viewer.shadowMap.size = 4096; // Increased from 2048 for higher resolution shadows
  viewer.shadowMap.darkness = 0.5;

  return viewer;
}

// Define the keyframes animation
const overlayFadeIn = keyframes`
  0% {
    opacity: 0;
    filter: blur(8px);
  }
  50% {
    opacity: 0.5;
    filter: blur(4px);
  }
  100% {
    opacity: 1;
    filter: blur(0.5px);
  }
`;

const blurAnimation = keyframes`
  0% {
    filter: blur(20px);
  }
  50% {
    filter: blur(10px);
  }
  100% {
    filter: blur(0px);
  }
`;

// Add loading overlay styling
const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1001;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Create styled component for the overlay
const ImageOverlay = styled.div<{ imageUrl: string }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1000;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
  animation: ${overlayFadeIn} 0.8s ease-out forwards;
  filter: blur(0.5px);
`;

const CesiumMapStyled = styled.div`
  /* animation: ${blurAnimation} 3.8s ease-out forwards; */
  /* filter: blur(0px); */
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
`;


const CesiumMap = ({ onCesiumCameraMove, cesiumCameraPosition, buildingStyle = 'default', distanceCenter, onDrawComplete, gisData, pinData,  onScreenshotTaken, geminiApiKey, mapStyle, enableTileCache, maxTileCacheSize, fogColor, fogDensity, fogByDistance, handleClickIdeaMarker,   shouldFlyTo
}: CesiumMapProps) => {
  const isMobile = useIsMobile()
  const viewerRef = useRef<any>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const isInitializedRef = useRef(false);
  const lastCameraPositionRef = useRef<CesiumMapProps['cesiumCameraPosition'] | null>(null);
  const osmBuildingsLoadedRef = useRef(false);
  const gisEntitiesRef = useRef<any[]>([]);
  const tilesetRef = useRef<any>(null); // Add a ref to store the tileset
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [ideaMarkerColor, setIdeaMarkerColor] = useState<string | null>(null);
  const ideaPinRef = useRef<HTMLDivElement>(null);
  // Add state to track viewer initialization
  const [isViewerReady, setIsViewerReady] = useState(false);
  // Add loading state to hide the initial globe view
  const [isLoading, setIsLoading] = useState(true);
  
  // Get the global state for Cesium map
  const [statefulCesiumMap, setStatefulCesiumMap] = useGlobalState().statefulCesiumMap;
  
  // Check if rendering mode is enabled
  const isRenderingMode = mapStyle === "cesium-rendering";
  
  // Add a function to normalize GIS data
  const normalizeGisData = useCallback((data: any[]) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map(item => {
      if (item.data) {
        return {
          ...item,
          data: {
            ...item.data,
            features: item.data.features || []
          }
        };
      }
      return item;
    });
  }, []);

  // Create a normalized version of gisData
  const normalizedGisData = useMemo(() => normalizeGisData(gisData || []), [gisData, normalizeGisData]);
  
  // Create a placeholder function for setGisData
  const setGisDataPlaceholder = useCallback((data: any) => {
    // This is a placeholder function since we don't need to update gisData here
    // The actual gisData is managed by the parent component
    console.log("setGisData called with:", data);
  }, []);
  
  // Create a placeholder for paddings
  const paddings = useMemo(() => ({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  }), []);
  
  // Call the useIdeaPinCesium hook at the top level of the component
  const ideaPinResult = useIdeaPinCesium({
    cesiumViewer: viewerRef.current,
    pinData,
    setIdeaMarkerColor,
    paddings,
    setGisData: setGisDataPlaceholder,
    normalizedGisData
  });
  
  // Use the result in a useEffect to update when the viewer is ready
  useEffect(() => {
    if (viewerRef.current && ideaPinResult) {
      // The hook has already been called, and it will update when pinData changes
      console.log("Idea pin hook initialized with viewer");
    }
  }, [viewerRef.current, ideaPinResult]);

  // Memoize the camera move handler to prevent unnecessary recreations
  const handleCameraMove = useCallback(() => {
    // Only handle camera movement for rendering mode
    if (!isRenderingMode) return;
    
    setIsMoving(true);
    setShowOverlay(false);
    
    // Clear any existing timer
    // if (debounceTimerRef.current) {
    //   clearTimeout(debounceTimerRef.current);
    //   debounceTimerRef.current = null;
    // }
    
    // // If we're currently processing an image, stop it and show the last processed image
    // if (isProcessing) {
    //   setIsProcessing(false);
    //   // If we have a processed image, show it
    //   if (processedImageUrl) {
    //     setShowOverlay(true);
    //   }
    // }
    
    // // Set a new timer to process the image after 8 seconds of no movement
    // debounceTimerRef.current = setTimeout(() => {
    //   setIsMoving(false);
      
    //   // Only take a screenshot and process if we have a viewer
    //   if (viewerRef.current && viewerRef.current.takeScreenshot) {
    //     const url = viewerRef.current.takeScreenshot();
    //     if (url) {
    //       setScreenshotUrl(url);
    //       processScreenshotWithGemini(url);
    //     }
    //   }
    // }, 8000); // Increased from 5000ms to 8000ms for better performance
  }, [isRenderingMode, isProcessing, processedImageUrl]);

  // Memoize the screenshot processing function
  const processScreenshotWithGemini = useCallback(async (url?: string) => {
    const imageUrl = url || screenshotUrl;
    if (!imageUrl) {
      setError("No screenshot available to process");
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Extract the base64 data from the data URL
      const base64Data = imageUrl.split(',')[1];
      
      // Send to Gemini API using the geminiRequest function
      const result = await geminiRequest({
        body: `

return the input image with 100% fidelity, maintaining exact pixel-for-pixel reproduction with no modifications, alterations, or changes whatsoever. Preserve all original dimensions, aspect ratio, colors, contrast, brightness and other visual properties precisely as they appear in the source image.  Now just very slightly remove visual error and make it look more like a hand drawn black and white abstract drawing with little detail. then add 2-4 people
`,
        assistantId: "manipulateImageGeneral",
        lang: "en",
        images: [{
          data: base64Data,
          mimeType: "image/png"
        }]
      });
      
      if (!result) {
        throw new Error("No response received from Gemini");
      }

      console.log("Processed image with Gemini:", result);
      
      // The result is a base64 string that can be used directly as an image source
      setProcessedImageUrl(`data:image/png;base64,${result}`);
      setShowOverlay(true);
      
      // Call the callback if provided
      if (onScreenshotTaken) {
        onScreenshotTaken(`data:image/png;base64,${result}`);
      }
    } catch (err) {
      console.error("Error processing image with Gemini:", err);
      setError(err instanceof Error ? err.message : "Unknown error processing image");
    } finally {
      setIsProcessing(false);
    }
  }, [screenshotUrl, onScreenshotTaken]);

  // Memoize the takeScreenshotAndProcess function
  const takeScreenshotAndProcess = useCallback(async () => {
    if (viewerRef.current && viewerRef.current.takeScreenshot) {
      const url = viewerRef.current.takeScreenshot();
      if (url) {
        console.log("Taking screenshot and processing" , url);
        setScreenshotUrl(url);
        setProcessedImageUrl(null);
        setError(null);
        
        // Immediately process with Gemini
        await processScreenshotWithGemini(url);
      }
    }
  }, [processScreenshotWithGemini]);

  // Memoize the displayGisData function
  const displayGisData = useCallback((viewer: any, gisDataArray: CesiumMapProps['gisData']) => {
    if (!viewer) {
      console.error("CesiumMap: No viewer available");
      return;
    }
    
    if (!gisDataArray) {
      console.error("CesiumMap: No gisData provided");
      return;
    }
    
    if (!Array.isArray(gisDataArray)) {
      console.error("CesiumMap: gisData is not an array", typeof gisDataArray);
      return;
    }

    // Clear existing GIS entities - use a more efficient batch removal
    if (gisEntitiesRef.current.length > 0) {
      viewer.entities.removeAll();
      gisEntitiesRef.current = [];
    }

    // Set up click handler for the viewer
    if (!viewer.screenSpaceEventHandler) {
      viewer.screenSpaceEventHandler = new window.Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    }
    
    // Remove any existing click handlers to prevent duplicates
    viewer.screenSpaceEventHandler.removeInputAction(window.Cesium.ScreenSpaceEventType.LEFT_CLICK);
    
    // Add new click handler
    viewer.screenSpaceEventHandler.setInputAction((click: any) => {
      // Reduce logging for performance
      // console.log("Click detected at:", click.position);
      const pickedObject = viewer.scene.pick(click.position);
      // console.log("Picked object:", pickedObject);
      
      if (window.Cesium.defined(pickedObject) && pickedObject.id && pickedObject.id.properties) {
        const properties = pickedObject.id.properties;
        // console.log("Clicked entity properties:", properties);
        
        // Check if the clicked entity has an ideaId
        if (properties.ideaId) {
          // Call the handler if it exists
          if (handleClickIdeaMarker) {
            handleClickIdeaMarker(properties.ideaId);
          }
        }
      }
    }, window.Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Process each GIS data item
    gisDataArray.forEach((gisItem, index) => {
      
      if (!gisItem) {
        console.error(`CesiumMap: GIS item ${index} is null or undefined`);
        return;
      }
      
      // Check if gisItem is a FeatureCollection directly
      let featureCollection;
      let layerType;
      let layerName;
      
      if (gisItem.type === 'FeatureCollection' && gisItem.features) {
        // This is a direct FeatureCollection
        featureCollection = gisItem;
        layerType = 'default';
        layerName = `Layer ${index}`;
      } else if (gisItem.data) {
        // This is the expected format with a data property
        featureCollection = gisItem.data;
        layerType = gisItem.layerType || 'default';
        layerName = gisItem.layerName || `Layer ${index}`;
      } else {
        console.error(`CesiumMap: GIS item ${index} has no valid data structure`, gisItem);
        return;
      }
      
      if (!featureCollection.features) {
        console.error(`CesiumMap: Feature collection has no features`, featureCollection);
        return;
      }
      
      // Define colors based on layer type
      let color;

      if(!layerType && featureCollection.features?.length){
        color = window.Cesium.Color.RED;
      }

      switch (layerType) {
        case 'polygon':
          color = window.Cesium.Color.BLUE.withAlpha(0.3);
          break;
        case 'line':
          color = window.Cesium.Color.GREEN;
          break;
        case 'point':
          color = window.Cesium.Color.RED;
          break;
        default:
          // Use different colors for different layers
          const colors = [
            window.Cesium.Color.BLUE,
            window.Cesium.Color.GREEN,
            window.Cesium.Color.RED,
            window.Cesium.Color.YELLOW,
            window.Cesium.Color.PURPLE,
            window.Cesium.Color.ORANGE
          ];
          color = colors[index % colors.length];
      }
      
      // Batch entity creation for better performance
      const entitiesToAdd: any[] = [];
      
      // OPTIMIZATION: Limit the number of features to display for better performance
      const maxFeatures = 1000;
      const featuresToProcess = featureCollection.features.length > maxFeatures 
        ? featureCollection.features.slice(0, maxFeatures) 
        : featureCollection.features;

      featuresToProcess.forEach((feature: Feature, featureIndex: number) => {        
        if (!feature.geometry) {
          console.error(`CesiumMap: Feature ${featureIndex} has no geometry`);
          return;
        }
        
        let positions: Position[] = []; // Initialize positions as an empty array
        let entitySkipped = false; // Flag to track if entity was skipped
        
        if (feature.geometry.type === 'Polygon') {
          positions = feature.geometry.coordinates[0]; // For polygons, use the outer ring
        } else if (feature.geometry.type === 'LineString') {
          positions = feature.geometry.coordinates;
        } else if (feature.geometry.type === 'Point') {
          positions = [feature.geometry.coordinates];
        } else {
          // Safely access the geometry type with optional chaining and provide a fallback
          const geometryType = feature.geometry && 'type' in feature.geometry ? feature.geometry.type : 'unknown';
          console.error(`CesiumMap: Unsupported geometry type: ${geometryType}`);
          entitySkipped = true; // Mark that we've skipped this entity
        }

        // Skip the rest of the processing if the entity was skipped
        if (entitySkipped) {
          return;
        }

        // Convert coordinates to Cesium positions
        const cesiumPositions = positions.map((coord: Position) => {
          return window.Cesium.Cartesian3.fromDegrees(coord[0], coord[1]);
        });
        
        // Get color from feature properties if available
        let entityColor = color;
        if (feature.properties?.color) {
          try {
            entityColor = window.Cesium.Color.fromCssColorString(feature.properties.color);
          } catch (error) {
            console.warn(`CesiumMap: Invalid color in feature properties: ${feature.properties.color}, using default color`);
          }
        }

        let entity;
        let entityAddedDirectly = false; // Flag to track if entity was added directly
        
        switch (feature.geometry.type) {
          case 'Polygon':
            entity = {
              name: layerName || `Polygon ${index}`,
              polygon: {
                hierarchy: new window.Cesium.PolygonHierarchy(cesiumPositions),
                material: entityColor.withAlpha(0.3),
                outline: true,
                outlineColor: entityColor,
              }
            };
            break;
          case 'LineString':
            entity = {
              name: layerName || `Line ${index}`,
              polyline: {
                positions: cesiumPositions,
                width: 4,
                material: new window.Cesium.PolylineGlowMaterialProperty({
                  glowPower: 0.15,
                  color: entityColor,
                }),
                clampToGround: true,
              }
            };
            break;
          case 'Point':
            entity = {
              name: layerName || `Point ${index}`,
              position: cesiumPositions[0],
              point: {
                pixelSize: 10,
                color: entityColor,
                outlineColor: window.Cesium.Color.WHITE,
                outlineWidth: 2,
                heightReference: window.Cesium.HeightReference.RELATIVE_TO_GROUND,
                height: 1,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
              },
              properties: feature.properties,
              cursor: 'pointer'
            };

            const pointEntity = viewer.entities.add(entity);
            gisEntitiesRef.current.push(pointEntity);
            entityAddedDirectly = true; // Mark that we've added the entity directly
            break;
          default:
            // Safely access the geometry type with type checking
            const geometryType = feature.geometry && typeof feature.geometry === 'object' && 'type' in feature.geometry 
              ? (feature.geometry as Geometry).type 
              : 'unknown';
            console.error(`CesiumMap: Unsupported geometry type: ${geometryType}`);
            entitySkipped = true; // Mark that we've skipped this entity
            break;
        }

        if (entity && !entityAddedDirectly) {
          entitiesToAdd.push(entity);
        } else if (!entityAddedDirectly) {
          console.error(`CesiumMap: Failed to create entity for feature ${featureIndex}`);
        }
      });
      
      // Batch add entities for better performance
      if (entitiesToAdd.length > 0) {
        const addedEntities = viewer.entities.add(entitiesToAdd);
        // Check if addedEntities is an array or a single entity
        if (Array.isArray(addedEntities)) {
          gisEntitiesRef.current.push(...addedEntities);
        } else {
          // If it's a single entity, just push it
          gisEntitiesRef.current.push(addedEntities);
        }
      }
    });
    
    // Request a render after adding all entities
    viewer.scene.requestRender();
  }, []);

  // Add a new ref for tracking staged loading state
  const stagingRef = useRef<{
    initialLoadComplete: boolean;
    qualityUpgradeComplete: boolean;
    initialRenderTimestamp: number;
    terrainUpgraded: boolean;
  }>({
    initialLoadComplete: false,
    qualityUpgradeComplete: false,
    initialRenderTimestamp: 0,
    terrainUpgraded: false
  });

  // Add a ref to track if we've handled the initial camera position
  const initialCameraSetRef = useRef(false);

  // Now we need to modify the initializeViewer function to use our new wrapper function
  // Since we can't directly modify the initializeViewer function (it's defined outside the component),
  // we'll create a wrapper function that calls initializeViewer and then sets up the center marker
  const initializeViewerWithCenterTracking = useCallback(async (
    Cesium: any,
    isMobile: boolean,
    onCesiumCameraMove?: CesiumMapProps['onCesiumCameraMove'],
    cesiumCameraPosition?: CesiumMapProps['cesiumCameraPosition'],
    osmBuildingsLoadedRef?: React.MutableRefObject<boolean>,
    onScreenshotTaken?: CesiumMapProps['onScreenshotTaken'],
    enableTileCache: boolean = true,
    maxTileCacheSize: number = 512,
    fogColor?: string,
    fogDensity?: number,
    fogByDistance?: { start: number; startValue: number; end: number; endValue: number }
  ) => {
    const viewer = await initializeViewer(
      Cesium,
      isMobile,
      onCesiumCameraMove,
      cesiumCameraPosition,
      osmBuildingsLoadedRef,
      onScreenshotTaken,
      enableTileCache,
      maxTileCacheSize,
      fogColor,
      fogDensity,
      fogByDistance
    );
  
    
    // Add a custom event listener for camera move end to track center coordinates
    viewer.camera.moveEnd.addEventListener(() => {
      // Get the center position on the terrain
      const centerScreenPosition = new Cesium.Cartesian2(
        viewer.canvas.clientWidth / 2,
        viewer.canvas.clientHeight / 2
      );
      
      // Get the position on the terrain
      const centerPosition = viewer.scene.pickPosition(centerScreenPosition);
      
      if (centerPosition) {
        // Convert to geographic coordinates
        const centerCartographic = Cesium.Cartographic.fromCartesian(centerPosition);
        const centerLongitude = Cesium.Math.toDegrees(centerCartographic.longitude);
        const centerLatitude = Cesium.Math.toDegrees(centerCartographic.latitude);
        
        setStatefulCesiumMap({
          ...statefulCesiumMap,
          centerCoordinates: {
            latitude:centerLatitude,
            longitude: centerLongitude
          },
          viewer: viewerRef.current
          // Don't update the viewer on every camera move to avoid performance issues
        });
        
        const cartographic = Cesium.Cartographic.fromCartesian(viewer.camera.position);
    const cameraPosition: {
          center: [number, number];
          pitch: number;
          bearing: number;
          height: number;
        } = {
      center: [Cesium.Math.toDegrees(cartographic.longitude), Cesium.Math.toDegrees(cartographic.latitude)],
      height: cartographic.height,
      bearing: Cesium.Math.toDegrees(viewer.camera.heading),
      pitch: Cesium.Math.toDegrees(viewer.camera.pitch),
    }
        // const cameraPosition: {
        //   center: [number, number];
        //   pitch: number;
        //   bearing: number;
        //   height: number;
        // } = {
        //   center: [centerLongitude, centerLatitude] as [number, number],
        //   pitch: viewer.camera.pitch,
        //   bearing: viewer.camera.heading,
        //   height: viewer.camera.positionCartographic.height
        // };
        
        console.log('Calling onCesiumCameraMove with:', cameraPosition);
        console.log('onCesiumCameraMove is defined:', !!onCesiumCameraMove);
        
        onCesiumCameraMove?.(cameraPosition);
      }
    });
    
    return viewer;
  }, [statefulCesiumMap, setStatefulCesiumMap]);

  // Enhanced function to initialize viewer with base color set to black
  useEffect(() => {
    // Only initialize once
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    console.log("CesiumMap: Initializing viewer with optimized loading strategy");
    stagingRef.current.initialRenderTimestamp = performance.now();
    
    // OPTIMIZATION: Set up performance monitoring
    const reportPerformance = () => {
      if (!stagingRef.current.initialLoadComplete) {
        stagingRef.current.initialLoadComplete = true;
        const loadTime = performance.now() - stagingRef.current.initialRenderTimestamp;
        console.log(`CesiumMap: Initial load completed in ${loadTime.toFixed(0)}ms`);
      }
    };

    // Check if Cesium is already loaded
    if (window.Cesium) {
      console.log("CesiumMap: Cesium already loaded, initializing viewer");
      initializeViewerWithCenterTracking(
        window.Cesium, 
        isMobile, 
        onCesiumCameraMove, 
        cesiumCameraPosition, 
        osmBuildingsLoadedRef, 
        isRenderingMode ? onScreenshotTaken : undefined, 
        enableTileCache, 
        maxTileCacheSize, 
        fogColor, 
        fogDensity, 
        fogByDistance
      ).then(async viewer => {
        // Set the globe's base color to black to hide during loading
        viewer.scene.globe.baseColor = new window.Cesium.Color(0, 0, 0, 1);
        
        viewerRef.current = viewer;
        
        // Store the viewer in the global state
        setStatefulCesiumMap({
          ...statefulCesiumMap,
          viewer: viewerRef.current
        });
        
        // Get the tileset reference
        try {
          const tileset = await window.Cesium.Cesium3DTileset.fromIonAssetId(3279851, {
            maximumScreenSpaceError: 32,
            // shadows: import.meta.env.VITE_APP_MODE === "development" ? false : window.Cesium.ShadowMode.ENABLED,
          });
          
          tilesetRef.current = tileset;
          viewer.scene.primitives.add(tileset);
          console.log("CesiumMap: Tileset loaded and added to scene");
        } catch (error) {
          console.error("CesiumMap: Error loading tileset", error);
        }
        
        // Mark viewer as ready after tileset is loaded
        setIsViewerReady(true);
        
        // Load OSM buildings
        try {
          console.log("CesiumMap: Loading OSM buildings");
          setTimeout(() => {
            viewer.loadOsmBuildings();
          }, 2000);
        } catch (error) {
          console.error("CesiumMap: Error loading OSM buildings", error);
        }
        
        // Set loading to false only after tileset is fully loaded and camera is positioned
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
        
        reportPerformance();
        
        // Add camera move start event listener only for rendering mode
        if (isRenderingMode) {
          viewer.camera.moveStart.addEventListener(() => {
            handleCameraMove();
          });
        }
        
        // If we already have GIS data, display it after initialization
        if (gisData) {
          console.log("CesiumMap: Displaying initial GIS data", gisData);
          setTimeout(() => {
            if (viewer && !viewer.isDestroyed()) {
              displayGisData(viewer, gisData);
            }
          }, 2000); // Slightly longer delay for GIS data
        }
      });
      return;
    }

 

    // Only create and append the script if it doesn't exist
    if (!scriptRef.current) {
      // OPTIMIZATION: Load script with higher priority
      const script = document.createElement("script");
      script.src = "https://cesium.com/downloads/cesiumjs/releases/1.107/Build/Cesium/Cesium.js";
      script.async = false; // Load synchronously for faster initial load
      script.setAttribute('importance', 'high'); // Hint to browser this is important
      scriptRef.current = script;

      // Add Cesium CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cesium.com/downloads/cesiumjs/releases/1.107/Build/Cesium/Widgets/widgets.css';
      document.head.appendChild(link);

      script.onload = () => {
        if (window.Cesium) {
          window.Cesium.Ion.defaultAccessToken =
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1MjI3NTNhZC0zNTljLTRlNWItOTc0ZS0yYjNjODhlZWU1OGYiLCJpZCI6MTk0OTI5LCJpYXQiOjE3MDc1MTE5NjR9.bW1to-Dax47Ra0BiGefax1eFdsOkPvWSaXSjCodQHgE";

          initializeViewerWithCenterTracking(
            window.Cesium, 
            isMobile, 
            onCesiumCameraMove, 
            cesiumCameraPosition, 
            osmBuildingsLoadedRef, 
            isRenderingMode ? onScreenshotTaken : undefined, 
            enableTileCache, 
            maxTileCacheSize, 
            fogColor, 
            fogDensity, 
            fogByDistance
          ).then(async viewer => {
            console.log("CesiumMap: Viewer initialized");
            
            // Set the globe's base color to black to hide during loading
            viewer.scene.globe.baseColor = new window.Cesium.Color(0, 0, 0, 1);
            
            viewerRef.current = viewer;
            
            // Store the viewer in the global state
            setStatefulCesiumMap({
              ...statefulCesiumMap,
              viewer: viewerRef.current
            });
            
            // Get the tileset reference
            try {
              const tileset = await window.Cesium.Cesium3DTileset.fromIonAssetId(3279851, {
                maximumScreenSpaceError: 32,
                // shadows: import.meta.env.VITE_APP_MODE === "development" ? false : window.Cesium.ShadowMode.ENABLED,
              });
              
              tilesetRef.current = tileset;
              viewer.scene.primitives.add(tileset);
              console.log("CesiumMap: Tileset loaded and added to scene");
            } catch (error) {
              console.error("CesiumMap: Error loading tileset", error);
            }
            
            // Mark viewer as ready after tileset is loaded
            setIsViewerReady(true);
            
            // Load OSM buildings
            try {
              console.log("CesiumMap: Loading OSM buildings");
              setTimeout(() => {
                viewer.loadOsmBuildings();
              }, 2000);
            } catch (error) {
              console.error("CesiumMap: Error loading OSM buildings", error);
            }
            
            // Set loading to false only after tileset is fully loaded and camera is positioned
            setTimeout(() => {
              setIsLoading(false);
            }, 2500);
            
            reportPerformance();
            
            // Add camera move start event listener only for rendering mode
            if (isRenderingMode) {
              viewer.camera.moveStart.addEventListener(() => {
                handleCameraMove();
              });
            }
            
            // If we already have GIS data, display it after initialization
            if (gisData) {
              console.log("CesiumMap: Displaying initial GIS data");
              setTimeout(() => {
                if (viewer && !viewer.isDestroyed()) {
                  displayGisData(viewer, gisData);
                }
              }, 2000); // Slightly longer delay for GIS data
            }
          });
        }
      };

      // OPTIMIZATION: Add script to head instead of body for faster parsing
      document.head.appendChild(script);
    }

    return () => {
      // Clear the debounce timer when unmounting
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Only clean up if we're actually unmounting the component
      if (viewerRef.current && typeof viewerRef.current.destroy === 'function') {
        try {
          viewerRef.current.destroy();
        } catch (error) {
          // Silent error handling
        }
        viewerRef.current = null;
      }

      // Don't remove the script as it might be needed by other instances
      // Just clear the reference
      scriptRef.current = null;
      isInitializedRef.current = false;
    };
  }, [isMobile, onCesiumCameraMove, cesiumCameraPosition, osmBuildingsLoadedRef, isRenderingMode, onScreenshotTaken, enableTileCache, maxTileCacheSize, fogColor, fogDensity, fogByDistance, handleCameraMove, displayGisData, gisData, setStatefulCesiumMap, initializeViewerWithCenterTracking]);


  useEffect(()=>{


    console.log("pinData",pinData)

  },[pinData])

  // Add a useEffect to handle gisData changes
  useEffect(() => {
    if (viewerRef.current && gisData) {
      console.log("CesiumMap: gisData changed, updating display", gisData);
      displayGisData(viewerRef.current, gisData);
    }
  }, [gisData, displayGisData]);

  // Add a new useEffect to handle mapStyle changes
  // useEffect(() => {
  //   if (viewerRef.current && isRenderingMode) {
  //     console.log("CesiumMap: MapStyle changed to rendering mode, setting up event listeners");
      
  //     // Remove any existing event listeners to avoid duplicates
  //     try {
  //       viewerRef.current.camera.moveStart.removeEventListener(handleCameraMove);
  //     } catch (error) {
  //       // Ignore errors if the event listener doesn't exist
  //       console.log("No existing event listener to remove");
  //     }
      
  //     // Add the event listeners for rendering mode
  //     viewerRef.current.camera.moveStart.addEventListener(() => {
  //       handleCameraMove();
  //     });
      
  //     // // If we're in rendering mode and the camera position is set, take a screenshot
  //     // if (cesiumCameraPosition) {
  //     //   // Wait a bit for the camera to settle
  //     //   setTimeout(() => {
  //     //     if (viewerRef.current && viewerRef.current.takeScreenshot) {
  //     //       const url = viewerRef.current.takeScreenshot();
  //     //       if (url) {
  //     //         setScreenshotUrl(url);
  //     //         processScreenshotWithGemini(url);
  //     //       }
  //     //     }
  //     //   }, 1000);
  //     // }
  //   }
  // }, [mapStyle, isRenderingMode, handleCameraMove, processScreenshotWithGemini, cesiumCameraPosition]);

  // Update camera position when it changes
  // useEffect(() => {
  //   // Check if we need to wait for the viewer to be initialized
  //   if (!viewerRef.current || !window.Cesium) {
  //     // If the viewer isn't ready yet, set a flag to check again later
  //     initialCameraSetRef.current = false;
  //     return;
  //   }

  //   // Skip if the camera position hasn't changed
  //   if (lastCameraPositionRef.current && 
  //       lastCameraPositionRef.current.longitude === cesiumCameraPosition?.longitude &&
  //       lastCameraPositionRef.current.latitude === cesiumCameraPosition?.latitude &&
  //       lastCameraPositionRef.current.height === cesiumCameraPosition?.height &&
  //       lastCameraPositionRef.current.bearing === cesiumCameraPosition?.bearing &&
  //       lastCameraPositionRef.current.pitch === cesiumCameraPosition?.pitch) {
  //     return;
  //   }

  //   // Update the last camera position ref
  //   lastCameraPositionRef.current = cesiumCameraPosition;

  //   if (cesiumCameraPosition) {
  //     // Ensure height is appropriate - minimum 100 meters to prevent going underground
  //     const height = Math.max(100, cesiumCameraPosition.height || 1000);
      
  //     // Set isMoving to true when camera position changes
  //     if (isRenderingMode) {
  //       setIsMoving(true);
  //       setShowOverlay(false);
  //     }
      
  //     // Log that we're setting the camera position
  //     console.log('Setting camera position:', {
  //       longitude: cesiumCameraPosition.longitude,
  //       latitude: cesiumCameraPosition.latitude,
  //       height: height,
  //       bearing: cesiumCameraPosition.bearing,
  //       pitch: cesiumCameraPosition.pitch
  //     });
      
  //     console.log("CesiumMap: Flying to camera position", cesiumCameraPosition);
  //     viewerRef.current.camera.flyTo({
  //       destination: window.Cesium.Cartesian3.fromDegrees(
  //         cesiumCameraPosition.longitude,
  //         cesiumCameraPosition.latitude,
  //         height
  //       ),
  //       orientation: {
  //         heading: cesiumCameraPosition.bearing ? window.Cesium.Math.toRadians(cesiumCameraPosition.bearing) : 0,
  //         pitch: cesiumCameraPosition.pitch ? window.Cesium.Math.toRadians(cesiumCameraPosition.pitch) : -window.Cesium.Math.PI_OVER_TWO,
  //         roll: 0.0
  //       },
  //       duration: 2.0,
  //       complete: async () => {
  //         // Reload OSM buildings when camera position changes significantly
  //         // This helps ensure buildings are loaded in the new view area
  //         try {
  //           console.log("CesiumMap: Ensuring OSM buildings are loaded at new camera position");
  //           if (viewerRef.current && viewerRef.current.loadOsmBuildings) {
  //             setTimeout(() => {
  //               viewerRef.current.loadOsmBuildings();
  //             }, 2000);
  //           }
  //         } catch (error) {
  //           console.error("CesiumMap: Error reloading OSM buildings", error);
  //         }
  //       }
  //     });
      
  //     // After the camera movement is complete, set isMoving to false
  //     // if (isRenderingMode) {
  //     //   setTimeout(() => {
  //     //     setIsMoving(false);
          
  //     //     // Take a screenshot and process it
  //     //     if (viewerRef.current && viewerRef.current.takeScreenshot) {
  //     //       const url = viewerRef.current.takeScreenshot();
  //     //       if (url) {
  //     //         setScreenshotUrl(url);
  //     //         processScreenshotWithGemini(url);
  //     //       }
  //     //     }
  //     //   }, 2500); // Reduced from 2500ms to 1500ms for better performance
  //     // }
      
  //     // Mark that we've handled this camera position
  //     initialCameraSetRef.current = true;
  //   }
  // }, [cesiumCameraPosition, isRenderingMode, processScreenshotWithGemini]);

  // Handle initial camera position when viewer becomes ready
  useEffect(() => {
    // Only run when the viewer becomes ready
    if (isViewerReady && !initialCameraSetRef.current && viewerRef.current) {
      // Add a short delay to ensure everything is loaded
      setTimeout(async () => {
        if (cesiumCameraPosition) {
          

          
          viewerRef.current.camera.flyTo({
            destination: window.Cesium.Cartesian3.fromDegrees(
              cesiumCameraPosition.center[0],
              cesiumCameraPosition.center[1],
              cesiumCameraPosition.height
            ),
            orientation: {
              heading: cesiumCameraPosition.bearing ? window.Cesium.Math.toRadians(cesiumCameraPosition.bearing) : 0,
              pitch: cesiumCameraPosition.pitch ? window.Cesium.Math.toRadians(cesiumCameraPosition.pitch) : -window.Cesium.Math.PI_OVER_TWO,
              roll: 0.0
            },
            duration: 2.0
          });
        } else {
          console.log('Viewer is ready but no camera position provided, zooming to default view');
          
          // If we have a direct tileset reference, use it
          if (tilesetRef.current) {
            console.log('Using stored tileset reference for zoomTo');
            viewerRef.current.zoomTo(tilesetRef.current, new window.Cesium.HeadingPitchRange(0, -Math.PI/8, 0));
          } else {
            // Find the 3D tileset if available as a fallback
            console.log('Searching for tileset in primitives');
            const primitives = viewerRef.current.scene.primitives;
            let tileset = null;
            
            // Look for a Cesium3DTileset in the primitives collection
            for (let i = 0; i < primitives.length; i++) {
              const primitive = primitives.get(i);
              if (primitive && primitive.constructor && 
                  (primitive.constructor.name === 'Cesium3DTileset' || 
                   (typeof primitive === 'object' && primitive._url && primitive._url.includes('ion.cesium')))) {
                tileset = primitive;
                console.log('Found tileset in primitives', i);
                break;
              }
            }
            
            if (tileset) {
              // Zoom to the tileset with minimal animation
              console.log('Zooming to found tileset');
              viewerRef.current.zoomTo(tileset, new window.Cesium.HeadingPitchRange(0, -Math.PI/8, 0));
            } else {
              // If no tileset, set a default view of the Earth
              console.log('No tileset found, setting default Earth view');
              viewerRef.current.camera.flyTo({
                destination: window.Cesium.Cartesian3.fromDegrees(0, 0, 10000000),
                duration: 0
              });
            }
          }
        }
        
        // Make sure OSM buildings are loaded after camera is positioned
        try {
          console.log("CesiumMap: Ensuring OSM buildings are loaded at initial position");
          if (viewerRef.current && viewerRef.current.loadOsmBuildings) {
            setTimeout(() => {
              viewerRef.current.loadOsmBuildings();
            }, 1000);
          }
        } catch (error) {
          console.error("CesiumMap: Error loading OSM buildings at initial position", error);
        }
        
        // Mark that we've handled the initial camera position
        initialCameraSetRef.current = true;
        
        // Set loading to false after camera is positioned
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }, 1000); // Small delay to ensure everything is loaded
    }
  }, [isViewerReady, cesiumCameraPosition]);

  // Handle camera position changes after initial setup
  useEffect(() => {

    // Skip if the viewer isn't ready or if this is the initial setup
    if (!isViewerReady || !initialCameraSetRef.current || !viewerRef.current || !cesiumCameraPosition || !shouldFlyTo) {
      return;
    }

    // // Skip if the camera position hasn't changed
    // if (lastCameraPositionRef.current && 
    //     lastCameraPositionRef.current.center[0] === cesiumCameraPosition.center[0] &&
    //     lastCameraPositionRef.current.center[1] === cesiumCameraPosition.center[1] &&
    //     lastCameraPositionRef.current.height === cesiumCameraPosition.height &&
    //     lastCameraPositionRef.current.bearing === cesiumCameraPosition.bearing &&
    //     lastCameraPositionRef.current.pitch === cesiumCameraPosition.pitch) {
    //   return;
    // }

    // Update the last camera position ref
    // lastCameraPositionRef.current = cesiumCameraPosition;

    // Ensure height is appropriate - minimum 100 meters to prevent going underground
    // const height = Math.max(100, cesiumCameraPosition.height || 1000);

    // Log that we're setting the camera position
    // console.log('Setting camera position:', {
    //   longitude: cesiumCameraPosition.center[0],
    //   latitude: cesiumCameraPosition.center[1],
    //   height: cesiumCameraPosition.height,
    //   bearing: cesiumCameraPosition.bearing,
    //   pitch: cesiumCameraPosition.pitch
    // });
    

    // Add 1 second delay before flying
    setTimeout(() => {
      viewerRef.current.camera.flyTo({
        destination: window.Cesium.Cartesian3.fromDegrees(
          shouldFlyTo.center[0],
          shouldFlyTo.center[1],
          shouldFlyTo.height
        ),
        orientation: {
          heading: shouldFlyTo.bearing ? window.Cesium.Math.toRadians(shouldFlyTo.bearing) : 0,
          pitch: shouldFlyTo.pitch ? window.Cesium.Math.toRadians(shouldFlyTo.pitch) : -window.Cesium.Math.PI_OVER_TWO,
          roll: 0.0
        },
        duration: 2.0,
        complete: async () => {
          // Reload OSM buildings when camera position changes significantly
          // This helps ensure buildings are loaded in the new view area
          try {
            console.log("CesiumMap: Ensuring OSM buildings are loaded at new camera position");
            if (viewerRef.current && viewerRef.current.loadOsmBuildings) {
              setTimeout(() => {
                viewerRef.current.loadOsmBuildings();
              }, 2000);
            }
          } catch (error) {
            console.error("CesiumMap: Error reloading OSM buildings", error);
          }
        }
      });
    }, 10);
    
 
  }, [shouldFlyTo, isRenderingMode, processScreenshotWithGemini, isViewerReady]);

  // Also modify the initializeViewer function to set the base color to black
  useEffect(() => {
    // Function to modify the globe base color
    const setGlobeColor = () => {
      if (viewerRef.current && window.Cesium) {
        // Set globe base color to black
        viewerRef.current.scene.globe.baseColor = new window.Cesium.Color(0, 0, 0, 1);
      }
    };
    
    // Apply immediately if viewer exists
    if (viewerRef.current) {
      setGlobeColor();
    }
  }, [isViewerReady]);

  // Add a method to update fog settings based on props
  useEffect(() => {
    if (viewerRef.current && viewerRef.current.updateFog) {
      viewerRef.current.updateFog({
        color: fogColor,
        density: fogDensity,
        byDistance: fogByDistance && window.Cesium ? new window.Cesium.Cartesian4(
          fogByDistance.start,
          fogByDistance.startValue,
          fogByDistance.end,
          fogByDistance.endValue
        ) : undefined
      });
    }
  }, [fogColor, fogDensity, fogByDistance]);

  // Memoize the screenshot button component to prevent unnecessary re-renders
  const ScreenshotButton = useMemo(() => (
    <button 
      onClick={takeScreenshotAndProcess}
      disabled={isProcessing}
      className={`px-3 py-1 rounded text-sm flex items-center gap-1 shadow-md z-50 ${
        isProcessing 
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
          : 'bg-green-500 text-white hover:bg-green-600'
      }`}
    >
      {isProcessing ? (
        <>
          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        </>
      ) : (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Process with Gemini
        </>
      )}
    </button>
  ), [takeScreenshotAndProcess, isProcessing]);

  // Memoize the error message component
  const ErrorMessage = useMemo(() => (
    error ? (
      <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
        {error}
      </div>
    ) : null
  ), [error]);

  return (
    <div className="relative w-full h-full" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
       {isLoading && (
        <LoadingOverlay className="bg-surface-subdued">
        </LoadingOverlay>
      )}
{/* 
      <CesiumDrawingFeature
        viewer={viewerRef.current}
        // onDrawComplete={handleDrawComplete}
        mapType={{mode: 'draw', drawType: 'path'}}
        // setDrawMode={setDrawMode}
      /> */}
      
      <CesiumMapStyled id="cesiumContainer" className="w-full h-full absolute inset-0" />
      {isRenderingMode && processedImageUrl && showOverlay  && (
        <div className="absolute inset-0 pointer-events-none z-10">
          <ImageOverlay imageUrl={processedImageUrl} />
        </div>
      )}
      
      {/* Screenshot controls - only show in rendering mode */}
      {isRenderingMode && (
        <div className="absolute bottom-4 right-4 z-10 flex flex-col items-end gap-2">
          {/* Take screenshot and process button */}
          {ScreenshotButton}
          
          {/* Error message */}
          {ErrorMessage}
        </div>
      )}
      {/* <MarkerPin visible={pinData} ref={ideaPinRef}>
        <IdeaPin transform={"translateY(-13px)"} color={ideaMarkerColor || undefined} />
      </MarkerPin> */}
    </div>
  );
};

export default CesiumMap;

// Add the MarkerPin styled component
const MarkerPin = styled.div<{ visible: any }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-100%);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: none;
  animation: showPin 0.4s ease-in-out;
  z-index: 1000;

  @keyframes showPin {
    0% {
      opacity: 0;
    }
    50%{
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;